/**
 * AgentVault MCP Server — serves agent skills as MCP tools.
 *
 * Uses Streamable HTTP transport mounted on the plugin's HTTP server.
 * Auth: SPT Bearer tokens validated via POST /capabilities/introspect.
 *
 * Design:
 * - One McpServer instance is created at startup and skills are registered as tools.
 * - Each incoming HTTP request creates a fresh StreamableHTTPServerTransport (stateless mode).
 * - After the transport handles the request, close() is called so the server can
 *   accept the next connection (Protocol enforces single-transport at a time).
 */
import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { StreamableHTTPServerTransport } from "@modelcontextprotocol/sdk/server/streamableHttp.js";
import type { IncomingMessage, ServerResponse } from "node:http";

/* -------------------------------------------------------------------------- */
/*  Public types                                                               */
/* -------------------------------------------------------------------------- */

export interface SkillDefinition {
  name: string;
  version?: string;
  description?: string;
  /** JSON Schema for the tool input (not Zod — raw JSON Schema object). */
  inputSchema?: Record<string, unknown>;
  /** Freeform instructions that are surfaced as an MCP prompt. */
  instructions?: string;
  /** SLA definition blob attached to the skill registry resource. */
  slaDefinition?: Record<string, unknown>;
  /** Tags for categorisation. */
  tags?: string[];

  /* -- agentVault namespace fields (from SKILL.md frontmatter) ------------- */

  /** Tools/capabilities explicitly allowed for this skill. */
  toolsAllowed?: string[];
  /** Tools/capabilities explicitly denied for this skill. */
  toolsDenied?: string[];
  /** JSON Schema for skill output validation. */
  outputSchema?: Record<string, unknown>;
  /** Model routing strategy: "auto" | "round-robin" | "least-latency". */
  modelRouting?: string;
  /** Allowed LLM models for this skill. */
  allowedModels?: string[];
  /** Default LLM model for this skill. */
  defaultModel?: string;
  /** AgentVault certification tier: "verified" | "certified" | "enterprise". */
  certificationTier?: string;
  /** Integrity configuration (algorithm, hashChain). */
  integrity?: Record<string, unknown>;
  /** Required policy presets (e.g., ["network: agentvault"]). */
  requiredPolicies?: string[];
}

export interface McpServerOpts {
  agentName: string;
  apiUrl: string;
  apiKey?: string;
  /**
   * Called when an MCP client invokes a registered skill tool.
   * Receives skill name, args, and the full skill definition (including instructions).
   * Return value is serialised as JSON and sent back as tool output.
   */
  onInvoke?: (skillName: string, args: Record<string, unknown>, skill: SkillDefinition) => Promise<unknown>;
}

/* -------------------------------------------------------------------------- */
/*  AgentVaultMcpServer                                                        */
/* -------------------------------------------------------------------------- */

export class AgentVaultMcpServer {
  private server: McpServer;
  private skills: Map<string, SkillDefinition> = new Map();
  private opts: McpServerOpts;
  private initialized = false;

  constructor(opts: McpServerOpts) {
    this.opts = opts;
    this.server = new McpServer({
      name: `agentvault-${opts.agentName}`,
      version: "1.0.0",
    });
  }

  /* ---- Skill registration ------------------------------------------------ */

  /**
   * Register a skill that will be exposed as an MCP tool.
   * Must be called *before* `initialize()`.
   */
  registerSkill(skill: SkillDefinition): void {
    this.skills.set(skill.name, skill);
  }

  /**
   * Register all skills as MCP tools / resources / prompts.
   * Called lazily on first request if not called explicitly.
   */
  initialize(): void {
    if (this.initialized) return;

    for (const [name, skill] of this.skills) {
      this.registerToolForSkill(name, skill);
    }

    // Registry resource — lists all registered skills as JSON.
    this.server.resource(
      "skills-registry",
      "skills://registry",
      { description: "List of all registered agent skills", mimeType: "application/json" },
      async () => {
        const registry = Array.from(this.skills.values()).map((s) => ({
          name: s.name,
          version: s.version,
          description: s.description,
          tags: s.tags,
          sla: s.slaDefinition,
          hasSchema: !!s.inputSchema,
          hasInstructions: !!s.instructions,
          certificationTier: s.certificationTier,
          modelRouting: s.modelRouting,
          allowedModels: s.allowedModels,
          hasToolPolicy: !!(s.toolsAllowed || s.toolsDenied),
          hasOutputSchema: !!s.outputSchema,
          requiredPolicies: s.requiredPolicies,
        }));
        return {
          contents: [{
            uri: "skills://registry",
            mimeType: "application/json",
            text: JSON.stringify(registry, null, 2),
          }],
        };
      },
    );

    // Skills that carry instructions are also surfaced as MCP prompts.
    for (const [name, skill] of this.skills) {
      if (skill.instructions) {
        this.server.prompt(
          name,
          skill.description ?? `Instructions for ${name}`,
          () => ({
            messages: [{
              role: "user" as const,
              content: { type: "text" as const, text: skill.instructions! },
            }],
          }),
        );
      }
    }

    this.initialized = true;
  }

  /* ---- HTTP request handler ---------------------------------------------- */

  /**
   * Handle an incoming HTTP request to the /mcp endpoint.
   *
   * Supports the Streamable HTTP transport protocol:
   * - POST for JSON-RPC messages
   * - GET  for SSE notification stream
   * - DELETE for session close
   *
   * Each request gets a fresh stateless transport; after the response is
   * flushed the transport + underlying protocol connection are torn down
   * so the single McpServer instance is ready for the next caller.
   *
   * Local requests from 127.0.0.1/::1 bypass SPT validation (owner access).
   */
  async handleRequest(req: IncomingMessage, res: ServerResponse): Promise<void> {
    if (!this.initialized) {
      this.initialize();
    }

    // --- Auth: localhost bypass for owner access, SPT for remote ---
    const remote = req.socket?.remoteAddress;
    const isLocal = remote === "127.0.0.1" || remote === "::1" || remote === "::ffff:127.0.0.1";

    if (!isLocal) {
      const authHeader = req.headers.authorization;
      if (!authHeader?.startsWith("Bearer ")) {
        res.writeHead(401, { "Content-Type": "application/json" });
        res.end(JSON.stringify({ error: "Missing or invalid Authorization header" }));
        return;
      }

      const token = authHeader.slice(7);
      const valid = await this.validateSpt(token);
      if (!valid) {
        res.writeHead(403, { "Content-Type": "application/json" });
        res.end(JSON.stringify({ error: "Invalid or expired SPT token" }));
        return;
      }
    }

    // --- Stateless transport per request ---
    const transport = new StreamableHTTPServerTransport({
      sessionIdGenerator: undefined, // stateless
    });

    await this.server.connect(transport);

    try {
      await transport.handleRequest(req, res);
    } finally {
      // Tear down so the McpServer can accept the next connection.
      // transport.close() triggers Protocol._onclose() which clears _transport.
      await transport.close();
    }
  }

  /* ---- Private helpers --------------------------------------------------- */

  /**
   * Register a single skill as an MCP tool.
   *
   * The MCP SDK's `tool()` overloads that accept a schema expect Zod types.
   * Since our skill definitions use raw JSON Schema we register without
   * schema validation (name + description + handler) and let the handler
   * receive the raw args object.
   */
  private registerToolForSkill(name: string, skill: SkillDefinition): void {
    const description = skill.description ?? `AgentVault skill: ${name}`;

    this.server.tool(
      name,
      description,
      async (args: Record<string, unknown>) => {
        if (!this.opts.onInvoke) {
          return {
            content: [{ type: "text" as const, text: `Skill "${name}" invoked but no handler registered` }],
          };
        }
        try {
          const result = await this.opts.onInvoke(name, args, skill);
          const text = typeof result === "string" ? result : JSON.stringify(result, null, 2);
          return {
            content: [{ type: "text" as const, text }],
          };
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : String(err);
          return {
            content: [{ type: "text" as const, text: `Error invoking skill "${name}": ${message}` }],
            isError: true,
          };
        }
      },
    );
  }

  /**
   * Validate a Service Provider Token against the AgentVault backend.
   */
  private async validateSpt(token: string): Promise<boolean> {
    try {
      const url = `${this.opts.apiUrl}/api/v1/capabilities/introspect`;
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (this.opts.apiKey) {
        headers["Authorization"] = `Bearer ${this.opts.apiKey}`;
      }
      const resp = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({ token }),
      });
      if (!resp.ok) return false;
      const data = (await resp.json()) as { active: boolean };
      return data.active === true;
    } catch {
      return false;
    }
  }

  /* ---- Accessors --------------------------------------------------------- */

  get skillCount(): number {
    return this.skills.size;
  }

  get isInitialized(): boolean {
    return this.initialized;
  }
}
